/**
 * CMS CONTENT endpoints (City / PopulerCity / CheapestDestination / Destination /
 * DestinationDetail / Campaign / CampaignDetail). See api/types/cmsContent.ts for the full
 * per-entity confirmation notes (swagger + live curl round-trips against
 * `http://localhost:1538/` on 2026-08-10) — request wrapper keys and route spellings below
 * (including the `Destionation` typo and the `Campaign` route vs. `AirCampaign` wire object)
 * are reproduced exactly as live-confirmed there.
 */
import { apiPost } from '../client';
import type {
  Campaign,
  CampaignDetail,
  CampaignDetailMutationResponse,
  CampaignListResponse,
  CheapestDestination,
  CheapestDestinationListResponse,
  City,
  CityListResponse,
  Destination,
  DestinationDetail,
  DestinationDetailMutationResponse,
  DestinationListResponse,
  PopulerCity,
  PopulerCityListResponse,
} from '../types/cmsContent';

/* --------------------------------- City ---------------------------------- */

export function getCities(siteId?: number): Promise<CityListResponse> {
  return apiPost<{ siteId?: number }, CityListResponse>('Cms/City/GetCities', { siteId });
}

export function getCityById(id: number): Promise<CityListResponse> {
  return apiPost<{ city: { id: number } }, CityListResponse>('Cms/City/GetCityById', { city: { id } });
}

export function addCity(city: Omit<City, 'id'>): Promise<CityListResponse> {
  return apiPost<{ city: Omit<City, 'id'> }, CityListResponse>('Cms/City/AddCity', { city });
}

export function editCity(city: City): Promise<CityListResponse> {
  return apiPost<{ city: City }, CityListResponse>('Cms/City/EditCity', { city });
}

export function deleteCity(id: number): Promise<CityListResponse> {
  return apiPost<{ city: { id: number } }, CityListResponse>('Cms/City/DeleteCity', { city: { id } });
}

/* ------------------------------ PopulerCity ------------------------------- */

export function getPopulerCities(siteId?: number): Promise<PopulerCityListResponse> {
  return apiPost<{ siteId?: number }, PopulerCityListResponse>('Cms/PopulerCity/GetPopulerCities', { siteId });
}

export function getPopulerCityById(id: number): Promise<PopulerCityListResponse> {
  return apiPost<{ populerCity: { id: number } }, PopulerCityListResponse>('Cms/PopulerCity/GetPopulerCityById', {
    populerCity: { id },
  });
}

export function addPopulerCity(populerCity: Omit<PopulerCity, 'id'>): Promise<PopulerCityListResponse> {
  return apiPost<{ populerCity: Omit<PopulerCity, 'id'> }, PopulerCityListResponse>('Cms/PopulerCity/AddPopulerCity', {
    populerCity,
  });
}

export function editPopulerCity(populerCity: PopulerCity): Promise<PopulerCityListResponse> {
  return apiPost<{ populerCity: PopulerCity }, PopulerCityListResponse>('Cms/PopulerCity/EditPopulerCity', {
    populerCity,
  });
}

export function deletePopulerCity(id: number): Promise<PopulerCityListResponse> {
  return apiPost<{ populerCity: { id: number } }, PopulerCityListResponse>('Cms/PopulerCity/DeletePopulerCity', {
    populerCity: { id },
  });
}

/* --------------------------- CheapestDestination ---------------------------
 * Route/request-wrapper spelling "Destionation" is the backend's own typo, not a mistake
 * here — see api/types/cmsContent.ts's file header.
 */

export function getCheapestDestinations(siteId?: number): Promise<CheapestDestinationListResponse> {
  return apiPost<{ siteId?: number }, CheapestDestinationListResponse>(
    'Cms/CheapestDestination/GetCheapestDestionations',
    { siteId }
  );
}

export function getCheapestDestinationById(id: number): Promise<CheapestDestinationListResponse> {
  return apiPost<{ cheapestDestionation: { id: number } }, CheapestDestinationListResponse>(
    'Cms/CheapestDestination/GetCheapestDestionationById',
    { cheapestDestionation: { id } }
  );
}

export function addCheapestDestination(
  cheapestDestionation: Omit<CheapestDestination, 'id'>
): Promise<CheapestDestinationListResponse> {
  return apiPost<{ cheapestDestionation: Omit<CheapestDestination, 'id'> }, CheapestDestinationListResponse>(
    'Cms/CheapestDestination/AddCheapestDestionation',
    { cheapestDestionation }
  );
}

export function editCheapestDestination(cheapestDestionation: CheapestDestination): Promise<CheapestDestinationListResponse> {
  return apiPost<{ cheapestDestionation: CheapestDestination }, CheapestDestinationListResponse>(
    'Cms/CheapestDestination/EditCheapestDestionation',
    { cheapestDestionation }
  );
}

export function deleteCheapestDestination(id: number): Promise<CheapestDestinationListResponse> {
  return apiPost<{ cheapestDestionation: { id: number } }, CheapestDestinationListResponse>(
    'Cms/CheapestDestination/DeleteCheapestDestionation',
    { cheapestDestionation: { id } }
  );
}

/* ------------------------------- Destination -------------------------------
 * No GetDestinationById route exists (confirmed) — edit works off the list row instead.
 */

export function getDestinations(siteId?: number): Promise<DestinationListResponse> {
  return apiPost<{ siteId?: number }, DestinationListResponse>('Cms/Destination/GetDestinations', { siteId });
}

export function addDestination(destination: Omit<Destination, 'id' | 'destinationDetails'>): Promise<DestinationListResponse> {
  return apiPost<{ destination: Omit<Destination, 'id' | 'destinationDetails'> }, DestinationListResponse>(
    'Cms/Destination/AddDestination',
    { destination }
  );
}

export function editDestination(
  destination: Omit<Destination, 'destinationDetails'>
): Promise<DestinationListResponse> {
  return apiPost<{ destination: Omit<Destination, 'destinationDetails'> }, DestinationListResponse>(
    'Cms/Destination/EditDestination',
    { destination }
  );
}

export function deleteDestination(id: number): Promise<DestinationListResponse> {
  return apiPost<{ destination: { id: number } }, DestinationListResponse>('Cms/Destination/DeleteDestination', {
    destination: { id },
  });
}

/* ---------------------------- DestinationDetail ----------------------------
 * No standalone list route exists (confirmed) — rows are read off the parent Destination's
 * `destinationDetails` array (see getDestinations above). Nested CRUD only.
 */

export function addDestinationDetail(
  destinationDetail: Omit<DestinationDetail, 'id'>
): Promise<DestinationDetailMutationResponse> {
  return apiPost<{ destinationDetail: Omit<DestinationDetail, 'id'> }, DestinationDetailMutationResponse>(
    'Cms/DestinationDetail/AddDestinationDetail',
    { destinationDetail }
  );
}

export function editDestinationDetail(destinationDetail: DestinationDetail): Promise<DestinationDetailMutationResponse> {
  return apiPost<{ destinationDetail: DestinationDetail }, DestinationDetailMutationResponse>(
    'Cms/DestinationDetail/EditDestinationDetail',
    { destinationDetail }
  );
}

export function deleteDestinationDetail(id: number): Promise<DestinationDetailMutationResponse> {
  return apiPost<{ destinationDetail: { id: number } }, DestinationDetailMutationResponse>(
    'Cms/DestinationDetail/DeleteDestinationDetail',
    { destinationDetail: { id } }
  );
}

/* --------------------------------- Campaign ---------------------------------
 * Wire-level business object/request-wrapper key is `airCampaign` (NOT `campaign`) despite
 * the route being `Cms/Campaign/*` — see api/types/cmsContent.ts's file header. No
 * GetCampaignById route exists — edit works off the list row.
 */

export function getCampaigns(siteId?: number): Promise<CampaignListResponse> {
  return apiPost<{ siteId?: number }, CampaignListResponse>('Cms/Campaign/GetCampaigns', { siteId });
}

export function addCampaign(airCampaign: Omit<Campaign, 'id' | 'airCampaignDetails'>): Promise<CampaignListResponse> {
  return apiPost<{ airCampaign: Omit<Campaign, 'id' | 'airCampaignDetails'> }, CampaignListResponse>(
    'Cms/Campaign/AddCampaign',
    { airCampaign }
  );
}

export function editCampaign(airCampaign: Omit<Campaign, 'airCampaignDetails'>): Promise<CampaignListResponse> {
  return apiPost<{ airCampaign: Omit<Campaign, 'airCampaignDetails'> }, CampaignListResponse>(
    'Cms/Campaign/EditCampaign',
    { airCampaign }
  );
}

export function deleteCampaign(id: number): Promise<CampaignListResponse> {
  return apiPost<{ airCampaign: { id: number } }, CampaignListResponse>('Cms/Campaign/DeleteCampaign', {
    airCampaign: { id },
  });
}

/* ------------------------------ CampaignDetail ------------------------------
 * No standalone list route exists (confirmed) — rows are read off the parent Campaign's
 * `airCampaignDetails` array (see getCampaigns above). Nested CRUD only.
 */

export function addCampaignDetail(airCampaignDetail: Omit<CampaignDetail, 'id'>): Promise<CampaignDetailMutationResponse> {
  return apiPost<{ airCampaignDetail: Omit<CampaignDetail, 'id'> }, CampaignDetailMutationResponse>(
    'Cms/CampaignDetail/AddCampaignDetail',
    { airCampaignDetail }
  );
}

export function editCampaignDetail(airCampaignDetail: CampaignDetail): Promise<CampaignDetailMutationResponse> {
  return apiPost<{ airCampaignDetail: CampaignDetail }, CampaignDetailMutationResponse>(
    'Cms/CampaignDetail/EditCampaignDetail',
    { airCampaignDetail }
  );
}

export function deleteCampaignDetail(id: number): Promise<CampaignDetailMutationResponse> {
  return apiPost<{ airCampaignDetail: { id: number } }, CampaignDetailMutationResponse>(
    'Cms/CampaignDetail/DeleteCampaignDetail',
    { airCampaignDetail: { id } }
  );
}
